import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Human-readable message from any thrown value. Supabase/Postgrest errors are
 * plain objects with a `.message` — not Error instances — so a bare
 * `instanceof Error` check reports "unknown" and hides the real cause.
 */
export function errorMessage(e: unknown, fallback = 'Something went wrong'): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && 'message' in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string' && m) return m;
  }
  if (typeof e === 'string' && e) return e;
  return fallback;
}

export function getSiteUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return import.meta.env.VITE_SITE_URL ?? 'http://localhost:5173';
}

export function publicCardUrl(slug: string): string {
  return `${getSiteUrl()}/u/${slug}`;
}

/**
 * Public-friendly slug. Allows letters, numbers, dashes, underscores, and slashes
 * so users can express paths like `personal/podcast` or `agency-2026`. Collapses
 * other characters into dashes and caps at 64 chars.
 *
 * By default we DON'T trim leading/trailing dashes or slashes — that breaks
 * mid-typing in controlled inputs (typing "roger-" would immediately become
 * "roger" and the next keystroke "p" would land as "rogerp" instead of
 * "roger-p"). Pass `{ trim: true }` on blur or save to clean up before
 * persisting.
 */
export function slugify(input: string, opts: { trim?: boolean } = {}): string {
  const base = input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9_/-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/\/{2,}/g, '/')
    .slice(0, 64);
  return opts.trim ? base.replace(/^[-/]+|[-/]+$/g, '') : base;
}
